import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { FileUpload, MyDocumentsCard } from '..'; // embedded documents components
import TimelineRoot from './TimelineRoot';
import DashboardHeader from './DashboardHeader';
import HealthDrawer from './HealthDrawer';
import useAcademicHealth from './useAcademicHealth';
import useUpcomingAssessments from './useUpcomingAssessments';
import './CenterColumn.css';

// CenterColumn: Home intelligence (greeting + upcoming + quick actions) or selected course detail (tabs: description, events/assignments, documents, etc.).

// Custom hook: Safe hook usage guard. If DataProvider not mounted above this subtree, avoid throwing.
function useSafeData() {
  try {
    return useData();
  } catch {
    return {}; // return empty object so destructuring is safe
  }
}

// Helper: Normalize event shape for EventDetails component
// EventDetails expects: { start, end, title, location, status, description, tags }
export function normalizeEventForModal(event) {
  if (!event) return null;
  
  // Derive start from various backend field names
  const start = event.start 
    || event.startDatetime 
    || event.startDate 
    || event.start_date 
    || event.eventDate 
    || event.dueDate;
  
  // Derive end from various backend field names
  const end = event.end 
    || event.endDatetime 
    || event.endDate 
    || event.end_date;
  
  return {
    ...event,
    start,
    end,
    title: event.title || event.name || 'Untitled Event',
    location: event.location || event.eventLocation || '',
    status: event.status || '',
    description: event.description || event.eventDescription || '',
    tags: event.tags || []
  };
}

// HomeView: Main dashboard home view with timeline and modals
function HomeView({ userName, weeklyProgress, upcomingEvents, upcomingItems, onTimelineReady }) {
  // Health drawer state (lifted from DashboardHeader)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = useCallback(() => setDrawerOpen(v => !v), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const health = useAcademicHealth();
  const upcomingAssessments = useUpcomingAssessments();

  // Modal state for assignment and event details — delegated to ScoutIQDashboard via CustomEvent.
  // CenterColumn no longer renders its own modals to avoid double-modal duplication.
  
  // Store timeline update functions
  const timelineUpdatersRef = useRef(null);
  const handleTimelineReady = useCallback((updaters) => {
    timelineUpdatersRef.current = updaters;
    // Forward to parent (ScoutIQDashboard) so create modals can access them
    if (onTimelineReady) {
      onTimelineReady(updaters);
    }
  }, [onTimelineReady]);
  
  // Handler to select assignment — dispatch to ScoutIQDashboard which owns the modals
  const handleAssignmentClick = useCallback((assignment) => {
    window.dispatchEvent(new CustomEvent('scoutiq:openAssignmentModal', { detail: { assignment } }));
  }, []);
  
  // Handler to select event — dispatch to ScoutIQDashboard which owns the modals
  const handleEventClick = useCallback((event) => {
    window.dispatchEvent(new CustomEvent('scoutiq:openEventModal', { detail: { event } }));
  }, []);
  
  return (
    <div className="center-column-container" role="main" aria-label="ScoutIQ Dashboard">
      <div className="center-column-content" data-tutorial="panel-center-content">
        <DashboardHeader userName={userName} drawerOpen={drawerOpen} onDrawerToggle={toggleDrawer} />
        {drawerOpen && (
          <HealthDrawer
            health={health}
            onClose={closeDrawer}
            assessments={upcomingAssessments.assessments}
            totalCount={upcomingAssessments.totalCount}
            onAssignmentClick={handleAssignmentClick}
            onEventClick={handleEventClick}
          />
        )}
        <div className="center-timeline-main">
          <div className="center-timeline-wrapper">
            <TimelineRoot 
              onAssignmentClick={handleAssignmentClick}
              onEventClick={handleEventClick}
              onTimelineReady={handleTimelineReady}
            />
          </div>
          <ActionToolbar />
        </div>
      </div>
    </div>
  );
}

export default function CenterColumn({
  selectedCourse,
  selectedCourseAssignments = [],
  selectedCourseEvents = [],
  selectedCourseSummary = null,
  userName = 'Student', // fallback; will be overridden by DataContext
  weeklyProgress = 0.72,
  upcomingEvents = [], // legacy prop
  upcomingItems = [],   // new unified upcoming items source
  onTimelineReady       // callback to receive timeline updaters
}) {
  const isCourseSelected = !!selectedCourse;

  // Resolve user name from DataContext (validateToken payload) with graceful fallbacks.
  // useSafeData is a custom hook wrapper that prevents crashes if the provider is absent.
  const { getCurrentUser } = useSafeData();
  const [resolvedUserName, setResolvedUserName] = useState(userName);

  useEffect(() => {
    let alive = true;
    async function fetchUser() {
      if (!getCurrentUser) return; // context not ready
      try {
        const user = await getCurrentUser();
        if (!alive || !user) return;
        const name = deriveDisplayName(user) || userName;
        setResolvedUserName(name);
      } catch (e) {
        // Silently ignore; keep fallback userName
        // console.warn('CenterColumn: failed to resolve user name from context', e);
      }
    }
    fetchUser();
    return () => { alive = false; };
  }, [getCurrentUser, userName]);

  return (
    <div className="scoutiq-center-column" aria-label="Center content column">
      {isCourseSelected ? (
        <CourseDetailPanel
          selectedCourse={selectedCourse}
          selectedCourseAssignments={selectedCourseAssignments}
          selectedCourseEvents={selectedCourseEvents}
          selectedCourseSummary={selectedCourseSummary}
        />
      ) : (
        <HomeView 
          userName={resolvedUserName} 
          weeklyProgress={weeklyProgress} 
          upcomingEvents={upcomingEvents} 
          upcomingItems={upcomingItems}
          onTimelineReady={onTimelineReady}
        />
      )}
    </div>
  );
}

function deriveDisplayName(user) {
  if (!user || typeof user !== 'object') return '';
  // Common field variants
  const usernameRaw = user.username || user.userName; // App-wide canonical username field preference
  const first = user.firstName || user.first_name || user.givenName || user.given_name;
  const full = user.name || user.fullName || user.full_name;
  const email = user.email;

  // Updated priority order (per requirement "use the username that we use across the app"):
  // 1. Username (exact, un-split) 2. First name 3. First token of full name 4. Email prefix
  if (usernameRaw) return usernameRaw; // show as-is to match global usage elsewhere
  if (first) return capitalize(first);
  if (full) return capitalize(full.split(/\s+/)[0]);
  if (email) return capitalize(email.split('@')[0]);
  return '';
}

function capitalize(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// === ACTION TOOLBAR ===

function ActionToolbar() {
  const navigate = useNavigate();
  
  const actions = [
    { icon: '+', label: 'Add Course', variant: 'secondary', onClick: () => navigate('/create-course-wizard') },
    { icon: '📋', label: 'Add Assignment', variant: 'secondary', onClick: () => {
      if (window.openCreateAssignmentModal) {
        window.openCreateAssignmentModal();
      }
    }},
    { icon: '📅', label: 'Add Event', variant: 'secondary', onClick: () => {
      if (window.openCreateEventModal) {
        window.openCreateEventModal();
      }
    }},
    { icon: '✏️', label: 'Open Planner', variant: 'primary', onClick: () => navigate('/calendar') }
  ];
  
  return (
    <div className="action-toolbar" aria-label="Quick actions">
      {actions.map((action, index) => (
        <button 
          key={index} 
          type="button" 
          className={`btn btn--${action.variant} btn--toolbar`}
          disabled={!action.onClick}
          onClick={action.onClick}
          aria-label={action.label}
        >
          <span className="btn__icon" aria-hidden="true">{action.icon}</span>
          <span className="btn__label">{action.label}</span>
        </button>
      ))}
    </div>
  );
}

// === Embedded Course Detail Panel with Tabs (Description, Chat, Events & Assignments, Calendar, Documents) ===
function CourseDetailPanel({ selectedCourse, selectedCourseAssignments, selectedCourseEvents, selectedCourseSummary }) {
  const [activeTab, setActiveTab] = useState('description'); // 'description' | 'chat' | 'eventsAssignments' | 'calendar' | 'documents'
  const overviewText = selectedCourse?.description || selectedCourseSummary?.text || 'No description available yet. TODO: Provide AI-enhanced course summary.';
  const rawAssignments = (selectedCourseAssignments && selectedCourseAssignments.length ? selectedCourseAssignments : mockAssignments());
  const rawEvents = (selectedCourseEvents && selectedCourseEvents.length ? selectedCourseEvents : mockEvents());
  // Assignment counts removed - now calculated locally in CourseActivityColumns from localAssignments for instant updates
  const { eventPast, eventUpcoming } = tallyEvents(rawEvents);
  
  // Debug logging for assignments
  console.log('[CourseDetailPanel] Assignments loaded:', {
    totalAssignments: rawAssignments.length,
    sampleAssignments: rawAssignments.slice(0, 3).map(a => ({
      id: a.id || a.assignmentId,
      title: a.title?.substring(0, 20),
      completed: a.completed,
      status: a.status
    }))
  });
  const courseKey = selectedCourse?.id || selectedCourse?.endeavorId || selectedCourse?.courseId || null;

  // Modal handlers — dispatch to ScoutIQDashboard which owns the modals
  const handleAssignmentClick = React.useCallback((assignment) => {
    window.dispatchEvent(new CustomEvent('scoutiq:openAssignmentModal', { detail: { assignment } }));
  }, []);

  const handleEventClick = React.useCallback((event) => {
    window.dispatchEvent(new CustomEvent('scoutiq:openEventModal', { detail: { event } }));
  }, []);

  return (
    <div className="scoutiq-course-panel" aria-label="Selected course intelligence panel">
      <CourseHeader
        course={selectedCourse}
        activeTab={activeTab}
        onChangeTab={setActiveTab}
        counts={{ eventPast, eventUpcoming }}
      />
      {/* Tab Panels */}
      {activeTab === 'description' && (
        <div className="scoutiq-course-body" aria-label="Course description panel">
          <p className="scoutiq-course-body__text" title={overviewText}>{overviewText}</p>
          <CourseActivityColumns
            courseId={courseKey}
            events={rawEvents}
            assignments={rawAssignments}
            counts={{ eventPast, eventUpcoming }}
            onAssignmentClick={handleAssignmentClick}
            onEventClick={handleEventClick}
          />
        </div>
      )}
      {activeTab === 'eventsAssignments' && (
        <div className="scoutiq-course-body" aria-label="Events & Assignments panel">
          <CourseActivityColumns
            courseId={courseKey}
            events={rawEvents}
            assignments={rawAssignments}
            counts={{ eventPast, eventUpcoming }}
            onAssignmentClick={handleAssignmentClick}
            onEventClick={handleEventClick}
          />
        </div>
      )}
      {activeTab === 'documents' && (
        <div className="scoutiq-course-body" aria-label="Course documents panel">
          {/* Pass explicit selected course id as override to guarantee endeavorId resolution */}
          <CourseDocumentsPanel course={selectedCourse} endeavorIdOverride={courseKey} />
        </div>
      )}
      {activeTab === 'chat' && (
        <div className="scoutiq-course-body" aria-label="Chat placeholder">
          <p className="scoutiq-course-body__text">Chat integration coming soon.</p>
        </div>
      )}
      {activeTab === 'calendar' && (
        <div className="scoutiq-course-body" aria-label="Calendar placeholder">
          <p className="scoutiq-course-body__text">Calendar view coming soon.</p>
        </div>
      )}
      <div className="scoutiq-action-card scoutiq-course-action-card" aria-label="Course actions">
        <div className="scoutiq-action-card__header">Survey My {selectedCourse?.shortName || selectedCourse?.code || 'Course'} Work</div>
        <div className="scoutiq-action-buttons">
          <button type="button" className="scoutiq-action-button" disabled><span>Generate Quiz</span></button>
          <button type="button" className="scoutiq-action-button" disabled><span>Create Cheat Sheet</span></button>
          <button type="button" className="scoutiq-action-button" disabled><span>Review My Essay Draft</span></button>
        </div>
      </div>
    </div>
  );
}

function CourseHeader({ course, activeTab, onChangeTab, counts }) {
  if (!course) return null;
  const {
    title, name, courseName, endeavorName, code, shortName,
    instructor, instructorName, professor,
    location, room, startDate, endDate, officeHours
  } = course;
  const displayTitle = title || name || courseName || endeavorName || 'Selected Course';
  const displayInstructor = instructorName || instructor || professor || null;
  const loc = room || location || null;
  const dateRange = (startDate && endDate) ? `${formatDateShort(startDate)} – ${formatDateShort(endDate)}` : null;
  const metaItems = [loc, dateRange, officeHours ? `Office Hours: ${officeHours}` : null].filter(Boolean);
  const displayCode = code || shortName || '';
  const tabs = [
    { key: 'description', label: 'Description' },
    { key: 'chat', label: 'Chat' },
    { key: 'eventsAssignments', label: 'Events & Assignments' },
    { key: 'calendar', label: 'Calendar' },
    { key: 'documents', label: 'Documents' }
  ];
  return (
    <div className="scoutiq-course-header" aria-label="Course header">
      <div className="scoutiq-course-header__top">
        <div className="scoutiq-course-header__titles">
          <h2 className="scoutiq-course-header__title" title={displayTitle}>{displayTitle}</h2>
          {displayInstructor && <div className="scoutiq-course-header__instructor" title={displayInstructor}>{displayInstructor}</div>}
          {metaItems.length > 0 && <div className="scoutiq-course-header__meta" title={metaItems.join(' · ')}>{metaItems.join(' · ')}</div>}
        </div>
        {displayCode && <span className="scoutiq-course-pill" title={displayCode}>{displayCode}</span>}
      </div>
      <div className="scoutiq-course-tabs" role="tablist" aria-label="Course navigation">
        {tabs.map(t => (
          <div
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            className={"scoutiq-course-tab" + (activeTab === t.key ? ' is-active' : '')}
            onClick={() => onChangeTab(t.key)}
          >{t.label}</div>
        ))}
      </div>
    </div>
  );
}

// Embedded Documents panel (mirrors DocumentsPage layout) scoped to selected course
function CourseDocumentsPanel({ course, endeavorIdOverride }) {
  // Resolve course scoped endeavorId (supports multiple naming variants + explicit override).
  const resolvedEndeavorId = (
    endeavorIdOverride ||
    course?.endeavorId || course?.endeavorID ||
    course?.id || course?.courseId || course?.courseID ||
    course?.endeavor_id || course?.endeavorID || null
  );

  // Local scope toggle: 'course' => pass endeavorId, 'all' => unscoped (show everything user-wide)
  const [scopeMode, setScopeMode] = useState('course'); // 'course' | 'all'
  const viewingAll = scopeMode === 'all';
  const effectiveEndeavorId = viewingAll ? undefined : resolvedEndeavorId; // omit prop to show all docs

  // (debug logging removed)

  // NOTE: MyDocumentsCard listens to the global 'documents:changed' CustomEvent fired by FileUpload.
  // Therefore we don't need explicit callbacks to refresh; it will auto-update on upload completion.
  return (
    <div className="scoutiq-course-documents-panel" aria-label="Course Documents">
      <div className="course-documents-panel-header">
        <h3 className="course-documents-title" title="Course Documents">Course Documents</h3>
        {/* Scope toggle */}
        <div className="documents-scope-toggle segmented-control" role="group" aria-label="Documents scope toggle">
          {['course','all'].map(mode => (
            <label key={mode} className="segment">
              <input
                type="radio"
                name="documents-scope"
                value={mode}
                checked={scopeMode === mode}
                onChange={() => setScopeMode(mode)}
                aria-label={mode === 'course' ? 'Show only this course\'s documents' : 'Show all my documents'}
              />
              <span className={"segment-text" + (scopeMode === mode ? ' active' : '')}>
                {mode === 'course' ? 'Course' : 'All'}
              </span>
            </label>
          ))}
        </div>
      </div>
      {!resolvedEndeavorId && scopeMode === 'course' && (
        <div style={{
          fontSize: '.6rem',
          marginBottom: '.75rem',
          padding: '.4rem .55rem',
          background: '#f8fafc',
          border: '1px dashed #dbe1e8',
          borderRadius: '8px'
        }}>
          No course identifier resolved; showing unscoped documents. Switch scope to "All" to view everything.
        </div>
      )}
      <div className="course-documents-grid">
        <div className="course-documents-upload" aria-label="Upload files for this course">
          {/* Keep uploads course-scoped even when viewing all so new files attach to selected course */}
          <FileUpload endeavorId={resolvedEndeavorId} />
        </div>
        <div className="course-documents-list" aria-label={viewingAll ? 'All my documents' : 'My documents for this course'}>
          <MyDocumentsCard endeavorId={effectiveEndeavorId} maxItems={100} />
        </div>
      </div>
    </div>
  );
}

// buildCourseHeader removed; replaced by CourseHeader component above.

function formatDateShort(value) {
  try { const d = new Date(value); if (Number.isNaN(d.getTime())) return ''; return `${d.toLocaleString(undefined,{ month:'short' })} ${d.getDate()}`; } catch { return ''; }
}

function mockAssignments() {
  return [
    { id:'a1', title:'Case Study Outline', dueDate:new Date(), tag:'First engagement' },
    { id:'a2', title:'Reading Reflection', dueDate:new Date(Date.now()+86400000), tag:'Reflection' }
  ];
}
function mockEvents() {
  return [
    { id:'e1', title:'Discussion Session', dueDate:new Date(Date.now()+3600000), tag:'Live' },
    { id:'e2', title:'Guest Speaker', dueDate:new Date(Date.now()+172800000), tag:'Speaker' }
  ];
}

// === Activity Column Helpers ===
function tallyEvents(list){
  const now = Date.now();
  let eventPast = 0; let eventUpcoming = 0;
  list.forEach(e => {
    const raw = e.startDatetime || e.eventDate || e.dueDate || e.due;
    if (!raw) { eventUpcoming++; return; }
    const dt = (typeof raw === 'string' ? new Date(raw.includes('T')? raw : raw+'T00:00:00') : new Date(raw)).getTime();
    if (isNaN(dt)) { eventUpcoming++; return; }
    if (dt < now) eventPast++; else eventUpcoming++;
  });
  return { eventPast, eventUpcoming };
}

// Fields to preserve from local state when merging incoming assignments
const PRESERVE_FIELDS = ["completed"];

function CourseActivityColumns({ courseId, events, assignments, counts, onAssignmentClick, onEventClick }){
  const [eventFilter, setEventFilter] = React.useState('upcoming'); // 'upcoming' | 'past' | 'all'
  // Simplified single assignments filter: 'all' | 'upcoming' | 'overdue'
  const [assignmentFilter, setAssignmentFilter] = React.useState('all');
  const filteredEvents = events.filter(ev => filterEvent(ev, eventFilter)).sort(sortEvents(eventFilter));
  // Local working copy of assignments so checkbox can toggle completion without mutating prop array
  // Deduplicate assignments by ID to prevent duplicate key warnings
  const deduplicatedAssignments = React.useMemo(() => {
    const seen = new Map();
    assignments.forEach(a => {
      const id = a.id || a.assignmentId;
      if (!seen.has(id)) {
        // Keep first occurrence (deduplication only)
        seen.set(id, a);
      }
    });
    const result = Array.from(seen.values());
    if (result.length !== assignments.length) {
      console.warn('[CourseActivityColumns] Removed duplicate assignments:', {
        original: assignments.length,
        deduplicated: result.length,
        duplicates: assignments.length - result.length
      });
    }
    return result;
  }, [assignments]);
  
  const [localAssignments, setLocalAssignments] = React.useState(deduplicatedAssignments);
  
  // ID normalization helper: ensures consistent string IDs across the component
  const getAssignmentId = React.useCallback((a) => {
    const id = a?.id ?? a?.assignmentId;
    if (id === undefined) {
      if (process.env.NODE_ENV !== 'production') {
        throw new Error('[CourseActivityColumns] Assignment missing ID: ' + JSON.stringify(a));
      }
      console.warn('[CourseActivityColumns] Assignment missing ID:', a);
      return undefined;
    }
    return String(id);
  }, []);
  
  // Merge helper: preserves local state for whitelisted fields while updating everything else
  const mergeAssignments = React.useCallback((prevList, incomingList) => {
    const prevById = new Map(prevList.map(a => [getAssignmentId(a), a]).filter(([id]) => id !== undefined));
    return incomingList
      .filter(a => getAssignmentId(a) !== undefined)
      .map(incoming => {
        const id = getAssignmentId(incoming);
        const prev = prevById.get(id);
        if (!prev) return incoming; // New assignment
        // Preserve whitelisted fields from local state, take everything else from incoming
        const preserved = Object.fromEntries(
          PRESERVE_FIELDS.map(field => [field, prev[field] ?? incoming[field]])
        );
        return { ...incoming, ...preserved };
      });
  }, [getAssignmentId]);
  
  // Track signature of incoming assignments to detect structural changes (add/remove)
  const lastIncomingSigRef = React.useRef(null);
  
  // Initialize localAssignments when course changes
  React.useEffect(() => {
    console.log('[CourseActivityColumns] Course changed, initializing assignments:', {
      courseId,
      assignmentCount: deduplicatedAssignments.length
    });
    setLocalAssignments(deduplicatedAssignments);
    lastIncomingSigRef.current = null; // Reset signature on course change
  }, [courseId, deduplicatedAssignments]);
  
  // Merge incoming assignments when structure changes (add/remove), preserving local toggles
  React.useEffect(() => {
    // Don't update during animations to prevent jumpiness
    const timeSinceLastToggle = Date.now() - (window.scoutiqLastToggleTime || 0);
    if (timeSinceLastToggle < 2000) {
      console.log('[CourseActivityColumns] Skipping merge - animation in progress');
      return;
    }
    
    const incomingIds = deduplicatedAssignments
      .map(getAssignmentId)
      .filter(Boolean)
      .sort()
      .join('|');
    
    if (lastIncomingSigRef.current === null) {
      // First render after course change, signature already initialized
      lastIncomingSigRef.current = incomingIds;
      return;
    }
    
    if (incomingIds !== lastIncomingSigRef.current) {
      console.log('[CourseActivityColumns] Assignment structure changed, merging:', {
        prevSignature: lastIncomingSigRef.current,
        newSignature: incomingIds,
        incomingCount: deduplicatedAssignments.length
      });
      setLocalAssignments(prev => mergeAssignments(prev, deduplicatedAssignments));
      lastIncomingSigRef.current = incomingIds;
    }
  }, [deduplicatedAssignments, mergeAssignments, getAssignmentId]);
  
  // Safe DataContext access (won't throw if provider absent)
  const { markAssignmentCompleted, updateAssignment, clearCache } = useSafeData();
  const pendingAssignmentIdsRef = React.useRef(new Set());
  
  // Handlers for Add buttons - open create modals
  const handleAddEvent = () => {
    if (window.openCreateEventModal) {
      window.openCreateEventModal();
    } else {
      console.warn('Create event modal handler not available');
    }
  };
  
  const handleAddAssignment = () => {
    if (window.openCreateAssignmentModal) {
      window.openCreateAssignmentModal();
    } else {
      console.warn('Create assignment modal handler not available');
    }
  };
  
  // Calculate completion percentages
  const totalEvents = (counts.eventPast || 0) + (counts.eventUpcoming || 0);
  const eventsCompletionPercentage = totalEvents > 0 
    ? Math.round((counts.eventPast / totalEvents) * 100) 
    : 0;
  
  // Calculate assignments progress from ALL assignments (not filtered view) using localAssignments for instant updates on toggle
  const totalAssignments = localAssignments.length;
  const completedAssignments = localAssignments.filter(a => a.completed || a.status === 'Completed' || a.submissionStatus === 'Completed').length;
  const assignmentsCompletionPercentage = totalAssignments > 0 
    ? Math.round((completedAssignments / totalAssignments) * 100) 
    : 0;
  
  console.log('[CourseActivityColumns] Progress calculated:', {
    totalAssignments,
    completedAssignments,
    percentage: assignmentsCompletionPercentage,
    localAssignmentsLength: localAssignments.length,
    sampleCompletionStatus: localAssignments.slice(0, 5).map(a => ({
      id: a.id || a.assignmentId,
      completed: a.completed,
      status: a.status
    }))
  });
  // Check if we should skip re-sorting to allow animations to play
  const timeSinceLastToggle = Date.now() - (window.scoutiqLastToggleTime || 0);
  const skipReSortForAnimation = timeSinceLastToggle < 2000; // Allow 2 seconds for animations
  
  // Cache the last filtered/sorted result to prevent expensive recalculations during animations
  const lastFilteredRef = React.useRef(null);
  
  // If animation is in progress, return cached result to prevent jumpy re-renders
  if (skipReSortForAnimation && lastFilteredRef.current !== null) {
    console.log('[CourseActivityColumns] Skipping filter/sort - animation in progress');
  }
  
  const filteredAssignments = (skipReSortForAnimation && lastFilteredRef.current !== null) 
    ? lastFilteredRef.current 
    : localAssignments
    .filter(a => {
      if (assignmentFilter === 'all') return true;
      const dueRaw = a.dueDatetime || a.dueDate || a.dueAt || a.due_at || a.due;
      if (!dueRaw) return assignmentFilter !== 'overdue'; // items without due date are not overdue
      const dt = new Date(dueRaw.includes?.('T') ? dueRaw : dueRaw + 'T00:00:00');
      if (isNaN(dt)) return assignmentFilter !== 'overdue';
      const now = new Date();
      if (assignmentFilter === 'upcoming') return dt >= now;
      if (assignmentFilter === 'overdue') return dt < now && !(a.completed || a.status === 'Completed' || a.submissionStatus === 'Completed');
      return true;
    })
    .sort((a,b)=>{
      // Always sort by completion status (incomplete first, complete last)
      const aCompleted = a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed';
      const bCompleted = b.completed === true || b.completed === 'true' || b.status === 'Completed' || b.submissionStatus === 'Completed';
      if (aCompleted !== bCompleted) {
        return aCompleted ? 1 : -1; // incomplete (false) comes before complete (true)
      }
      // Then by due date (chronological)
      const da = new Date((a.dueDatetime||a.dueDate||a.dueAt||a.due_at||a.due)||0).getTime();
      const db = new Date((b.dueDatetime||b.dueDate||b.dueAt||b.due_at||b.due)||0).getTime();
      return da - db;
    });
  
  // Cache the computed result for use during animation window
  if (!skipReSortForAnimation) {
    lastFilteredRef.current = filteredAssignments;
  }
  
  // Use a semantic alias per spec for clarity when rendering assignment items.
  const visibleAssignments = filteredAssignments;
  
  // Function to trigger confetti animation on assignment completion
  const triggerConfettiAnimation = (assignmentId) => {
    try {
      // Find the assignment card by its ID
      const assignmentCard = document.querySelector(`[data-assignment-id="${assignmentId}"]`) ||
                            document.querySelector(`[data-id="${assignmentId}"]`) ||
                            document.querySelector(`.assignment-card[data-assignment-id="${assignmentId}"]`);
      
      if (!assignmentCard) {
        console.log('Could not find assignment card for confetti animation:', assignmentId);
        return;
      }

      // Create confetti element
      const confetti = document.createElement('span');
      confetti.className = 'completion-confetti';
      confetti.setAttribute('aria-hidden', 'true');
      
      // Position confetti relative to the card
      assignmentCard.style.position = 'relative';
      assignmentCard.appendChild(confetti);
      
      // Remove confetti element after animation completes
      setTimeout(() => {
        if (confetti.parentNode) {
          confetti.parentNode.removeChild(confetti);
        }
      }, 1000); // Animation duration is 900ms, give it a bit extra
      
      console.log('Confetti animation triggered for assignment:', assignmentId);
    } catch (error) {
      console.error('Error triggering confetti animation:', error);
    }
  };
  
  async function handleToggleAssignmentCompleted(target){
    if (!target) {
      console.error('handleToggleAssignmentCompleted: No target assignment provided');
      return;
    }
    
    const id = target.id || target.assignmentId || target.assignmentID || target.assignment_id;
    console.log('handleToggleAssignmentCompleted called:', {
      target,
      id,
      markAssignmentCompleted: typeof markAssignmentCompleted,
      updateAssignment: typeof updateAssignment
    });
    
    if (!id) {
      console.error('handleToggleAssignmentCompleted: No valid ID found on target assignment');
      return;
    }
    
    if (pendingAssignmentIdsRef.current.has(id)) {
      console.log('handleToggleAssignmentCompleted: Request already pending for ID:', id);
      return; // prevent double clicks
    }
    
    // Determine current completion state
    const isCurrentlyCompleted = (target.completed || target.status === 'Completed' || target.submissionStatus === 'Completed');
    const willBeCompleted = !isCurrentlyCompleted;
    
    console.log('Assignment completion toggle:', {
      id,
      isCurrentlyCompleted,
      willBeCompleted,
      target
    });
    
    // DON'T update React state yet - let animations play on vanilla JS cards
    // We'll update after API call and animation complete
    let optimisticPrev = localAssignments;
    
    pendingAssignmentIdsRef.current.add(id);
    
    try {
      if (willBeCompleted) {
        console.log('Marking assignment as completed via API...');
        // Mark completed via dedicated endpoint if available
        if (typeof markAssignmentCompleted === 'function') {
          const resp = await markAssignmentCompleted(id);
          console.log('markAssignmentCompleted response:', resp);
          if (!resp?.ok) {
            const errorText = await resp.text().catch(() => 'Unknown error');
            throw new Error(`Failed to mark completed: ${resp.status} - ${errorText}`);
          }
        } else if (typeof updateAssignment === 'function') {
          const payload = { completed: true, status: 'Completed' };
          console.log('Using updateAssignment for completion:', payload);
          const resp = await updateAssignment(id, payload);
          console.log('updateAssignment response:', resp);
          if (!resp?.ok) {
            const errorText = await resp.text().catch(() => 'Unknown error');
            throw new Error(`Failed to update completion: ${resp.status} - ${errorText}`);
          }
        } else {
          throw new Error('No API methods available for marking assignment completed');
        }
      } else {
        console.log('Marking assignment as incomplete via API...');
        // Un-complete (no dedicated endpoint) -> updateAssignment
        if (typeof updateAssignment === 'function') {
          // Build comprehensive payload based on existing assignment data
          const dueRaw = target.dueAt || target.due_at || target.dueDate || target.dueDatetime || target.due;
          const overdue = isOverdue(dueRaw, false);
          const nextStatus = overdue ? 'overdue' : (dueRaw ? 'upcoming' : 'default');
          
          const payload = {
            assignmentId: id,
            title: target.title || target.name,
            description: target.description || null,
            assignedDate: target.assignedDate || null,
            dueDatetime: dueRaw,
            endeavorId: target.endeavorId || target.endeavorID || target.endeavor_id || null,
            eventId: target.eventId || target.eventID || target.event_id || null,
            priority: target.priority || null,
            completed: false,
            status: nextStatus,
            completedDatetime: null
          };
          
          console.log('Using updateAssignment for incompletion:', payload);
          const resp = await updateAssignment(id, payload);
          console.log('updateAssignment response:', resp);
          if (!resp?.ok) {
            const errorText = await resp.text().catch(() => 'Unknown error');
            throw new Error(`Failed to mark incomplete: ${resp.status} - ${errorText}`);
          }
        } else {
          throw new Error('No updateAssignment method available for marking assignment incomplete');
        }
      }
      
      console.log('[CourseActivityColumns] Assignment completion toggle successful!');
      
      // Clear cache to ensure fresh data on next fetch
      if (clearCache) {
        console.log('[CourseActivityColumns] Clearing caches for assignment', id);
        clearCache('assignments');
        clearCache(`assignment_${id}`);
        if (courseId) {
          clearCache(`endeavor_${courseId}_assignments`);
        }
      } else {
        console.warn('[CourseActivityColumns] clearCache not available!');
      }
      
      // DataContext markAssignmentCompleted/updateAssignment already handles cache updates and triggers dataRefreshTrigger
      
      // Trigger confetti animation if assignment was completed (not uncompleted)
      if (willBeCompleted) {
        triggerConfettiAnimation(id);
      }
      
      // Wait for animations to complete before updating React state
      // This prevents React from destroying the DOM elements mid-animation
      await new Promise(resolve => setTimeout(resolve, 900));
      
      // Now update React state to reflect the change and allow re-sorting
      setLocalAssignments(prev => {
        return prev.map(a => {
          const match = getAssignmentId(a) === getAssignmentId(target);
          if(!match) return a;
          const nextCompleted = willBeCompleted;
          const dueRaw = a.dueAt || a.due_at || a.dueDate || a.dueDatetime || a.due;
          let nextStatus;
          if(nextCompleted){
            nextStatus = 'Completed';
          } else {
            const overdue = isOverdue(dueRaw,false);
            nextStatus = overdue ? 'overdue' : (dueRaw ? 'upcoming' : 'default');
          }
          return { ...a, completed: nextCompleted, status: nextStatus };
        });
      });
      
    } catch (e) {
      console.error('Assignment completion toggle failed:', e);
      // Revert optimistic update on failure
      setLocalAssignments(optimisticPrev);
      // Show user-friendly error
      alert(`Failed to ${willBeCompleted ? 'complete' : 'incomplete'} assignment: ${e.message}`);
    } finally {
      pendingAssignmentIdsRef.current.delete(id);
    }
  }
  return (
    <div className="course-meta-grid" aria-label="Events and assignments">
  {/* Compact Events section (markup per new spec) */}
  <section className="course-section-card events-card course-events" aria-label="Events">
        <div className="ca-card-header">
          {/* Row 1: Title + Stats + Add Button */}
          <div className="ca-card-header-main">
            <div className="ca-card-title-block">
              <div className="ca-card-title-row">
                <span className="ca-card-icon" role="img" aria-label="Events">📅</span>
                <h3 className="ca-card-title">Events</h3>
              </div>
              <div className="ca-card-subtitle">
                {counts.eventPast} past, {counts.eventUpcoming} upcoming
              </div>
            </div>
            <button
              type="button"
              className="ca-add-button"
              onClick={handleAddEvent}
              aria-label="Add event"
            >
              + Add event
            </button>
          </div>
          
          {/* Row 2: Filter Pills */}
          <div className="ca-card-filters-row" role="tablist" aria-label="Event filters">
            {['upcoming','all','past'].map(mode => (
              <button
                key={mode}
                type="button"
                className={"filter-pill" + (eventFilter === mode ? " filter-pill-active" : "")}
                aria-pressed={eventFilter === mode}
                onClick={() => setEventFilter(mode)}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          
          {/* Row 3: Progress Bar */}
          <div className="ca-progress-row">
            <div className="ca-progress-bar-wrapper">
              <div className="scoutiq-course-progress-bar">
                <div 
                  className="scoutiq-course-progress-fill events" 
                  style={{ width: `${eventsCompletionPercentage}%` }}
                />
              </div>
            </div>
            <div className="ca-progress-percentage">
              {eventsCompletionPercentage}% of events completed
            </div>
          </div>
        </div>
        <div className="course-section-divider" />
        <div className="course-list event-list" data-kind="events">
          {filteredEvents.length ? filteredEvents.map(ev => (
            <EventCompactCard 
              key={ev.id || ev.eventId || ev.title} 
              event={ev} 
              courseId={courseId}
              onClick={onEventClick}
            />
          )) : <div className="events-empty">No events</div>}
        </div>
      </section>
  {/* Compact Assignments section */}
  <section className="course-section-card assignments-card course-assignments" aria-label="Assignments">
        <div className="ca-card-header">
          {/* Row 1: Title + Stats + Add Button */}
          <div className="ca-card-header-main">
            <div className="ca-card-title-block">
              <div className="ca-card-title-row">
                <span className="ca-card-icon" role="img" aria-label="Assignments">📝</span>
                <h3 className="ca-card-title">Assignments</h3>
              </div>
              <div className="ca-card-subtitle">
                {counts.assignmentCompleted} completed, {counts.assignmentRemaining} remaining
              </div>
            </div>
            <button
              type="button"
              className="ca-add-button"
              onClick={handleAddAssignment}
              aria-label="Add assignment"
            >
              + Add assignment
            </button>
          </div>
          
          {/* Row 2: Filter Pills */}
          <div className="ca-card-filters-row" aria-label="Assignment filters">
            {['all','upcoming','overdue'].map(mode => (
              <button
                key={mode}
                type="button"
                className={"filter-pill" + (assignmentFilter === mode ? " filter-pill-active" : "")}
                aria-pressed={assignmentFilter === mode}
                onClick={() => setAssignmentFilter(mode)}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          
          {/* Row 3: Progress Bar */}
          <div className="ca-progress-row">
            <div className="ca-progress-bar-wrapper">
              <div className="scoutiq-course-progress-bar">
                <div 
                  id="react-assignments-progress-fill"
                  className="scoutiq-course-progress-fill assignments" 
                  style={{ width: `${assignmentsCompletionPercentage}%` }}
                />
              </div>
            </div>
            <div className="ca-progress-percentage">
              {assignmentsCompletionPercentage}% of assignments completed
            </div>
          </div>
        </div>
        <div className="course-section-divider" />
        <div className="course-list assignment-list" data-kind="assignments" id="course-assignments-list">
          {/* React assignment cards disabled - vanilla JS handles rendering */}
          {false && visibleAssignments.map((assignment, idx) => {
            // Robust title/due fallbacks to avoid blank bars.
            const displayTitle = assignment.title || assignment.name || assignment.assignmentName || assignment.endeavorName || (() => {
              for (const [,v] of Object.entries(assignment)) {
                if (typeof v === 'string' && v.trim().length > 0 && v.length < 140 && /[a-zA-Z]/.test(v)) return v.trim();
              }
              return 'Untitled Assignment';
            })();
            const rawDue = assignment.dueAt || assignment.due_at || assignment.dueDate || assignment.dueDatetime || assignment.due || (() => {
              for (const [key,v] of Object.entries(assignment)) {
                if (/^(due|date|deadline)/i.test(key) && (typeof v === 'string' || typeof v === 'number')) {
                  const d = new Date(v);
                  if (!isNaN(d.getTime())) return v;
                }
              }
              return undefined;
            })();
            const status = assignment.status || (assignment.completed || assignment.submissionStatus === 'Completed' ? 'completed' : rawDue ? (isOverdue(rawDue,false) ? 'overdue' : 'upcoming') : 'default');
            // (debug logging removed)
            return (
              <div
                key={assignment.id || assignment.assignmentId || displayTitle + idx}
                className={`course-item-card assignment-card assignment-card--${status || 'default'}`}
                data-assignment-id={getAssignmentId(assignment)}
                onClick={(e) => {
                  console.log('🖱️ Assignment card div clicked', e.target, e.currentTarget);
                  onAssignmentClick && onAssignmentClick(assignment);
                }}
                style={{ cursor: 'pointer' }}
              >
                  <div className="course-item-card-body">
                    <div className="course-item-card-row">
                      <div className="course-item-card-title">{displayTitle}</div>
                      <label className="course-item-card-checkbox" onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                      }}>
                        <input
                          type="checkbox"
                          data-react-controlled="true"
                          data-assignment-id={getAssignmentId(assignment)}
                          checked={!!(assignment.completed || assignment.status === 'Completed' || assignment.submissionStatus === 'Completed')}
                          onChange={(e) => {
                            console.log('✅ [React] Assignment checkbox onChange:', {
                              assignment: assignment,
                              currentCompleted: assignment.completed,
                              currentStatus: assignment.status
                            });
                            e.preventDefault();
                            e.stopPropagation();
                            e.stopImmediatePropagation();
                            handleToggleAssignmentCompleted(assignment);
                          }}
                          onClick={(e) => {
                            console.log('✅ [React] Assignment checkbox onClick');
                            e.preventDefault();
                            e.stopPropagation();
                            e.stopImmediatePropagation();
                          }}
                          aria-label={displayTitle + ' completed'}
                        />
                        <span className="course-item-card-checkbox-ui" />
                      </label>
                    </div>
                    <div className="course-item-card-meta">{rawDue ? formatAssignmentDueDate({ dueDate: rawDue, dueAt: rawDue, dueDatetime: rawDue, due: rawDue }) : 'NO DUE DATE'}</div>
                  </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// New compact event card component per redesign
function EventCompactCard({ event, courseId, onClick }) {
  if (!event) return null;
  const title = event.title || event.name || 'Untitled Event';
  const rawDate = event.startDatetime || event.eventDate || event.dueDate || event.due;
  const dateDisplay = formatEventDate(rawDate);
  const status = isEventPast(rawDate) ? 'past' : (rawDate ? 'upcoming' : 'default');
  const eventId = event.eventId || event.id; // Extract event ID for wiring
  return (
    <div 
      className={`course-item-card event-card event-card--${status}`} 
      role="group" 
      aria-label={title} 
      data-course-id={courseId || ''}
      data-event-id={eventId}
      onClick={() => onClick && onClick(event)}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="course-item-card-body">
        <div className="course-item-card-title" title={title}>{title}</div>
        <div className="course-item-card-meta" title={dateDisplay}>{dateDisplay}</div>
      </div>
      {/* Placeholder checkbox to equalize layout with assignment cards */}
      <label className="course-item-card-checkbox is-placeholder" aria-hidden="true">
        <span className="course-item-card-checkbox-ui" />
      </label>
    </div>
  );
}
function isEventPast(raw){
  if(!raw) return false;
  try {
    const d = new Date(raw.includes?.('T')? raw : raw+'T00:00:00');
    if(isNaN(d)) return false;
    return d.getTime() < Date.now();
  } catch { return false; }
}
function formatEventDate(raw){
  if(!raw) return 'Date TBA';
  try {
    const d = new Date(raw.includes?.('T')? raw : raw+'T00:00:00');
    if(isNaN(d)) return 'Date TBA';
    return d.toLocaleString(undefined,{ weekday:'long', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' });
  } catch { return 'Date TBA'; }
}

function filterEvent(ev, mode){
  if (mode === 'all') return true;
  const raw = ev.startDatetime || ev.eventDate || ev.dueDate || ev.due;
  if (!raw) return mode === 'upcoming';
  const dt = new Date(raw.includes?.('T') ? raw : raw + 'T00:00:00');
  if (isNaN(dt)) return mode === 'upcoming';
  const now = new Date();
  return mode === 'past' ? dt < now : dt >= now;
}
function sortEvents(mode){
  return (a,b)=>{
    const da = new Date((a.startDatetime||a.eventDate||a.dueDate||a.due)||0).getTime();
    const db = new Date((b.startDatetime||b.eventDate||b.dueDate||b.due)||0).getTime();
    if (mode === 'past') return db - da; // recent first
    return da - db; // chronological
  };
}

// Per spec: use actual due date fields and render uppercase "MON DD HH:MM AM/PM" format
function formatAssignmentDueDate(assignment) {
  if (!assignment || typeof assignment !== 'object') return 'NO DUE DATE';
  const due = assignment.dueAt || assignment.due_at || assignment.dueDate || assignment.dueDatetime || assignment.due;
  if (!due) return 'NO DUE DATE';
  try {
    const d = new Date(due);
    if (isNaN(d.getTime())) return 'NO DUE DATE';
    const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const day = String(d.getDate()).padStart(2, '0');
    const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
    return `${month} ${day} ${time}`; // e.g. "OCT 21 11:59 PM"
  } catch {
    return 'NO DUE DATE';
  }
}

function isOverdue(raw, completed){
  if (!raw || completed) return false;
  try {
    const dt = new Date(raw.includes?.('T')? raw : raw+'T00:00:00');
    if(isNaN(dt)) return false;
    return dt.getTime() < Date.now();
  } catch { return false; }
}

